/* ==========================================================================
   HOPE BASE ADVISORY LLC — PUBLIC SHAREABLE DOCUMENT VIEWER (#doc/HBA-QUO-2026-0001)
   ========================================================================== */

const INVOICE_API = '/api/invoices.php';

export function initPublicDocViewer() {
  const publicOverlay = document.getElementById('public-doc-modal');
  const publicPrintBtn = document.getElementById('public-doc-print-btn');
  const publicCloseBtn = document.getElementById('public-doc-close-btn');

  // Check URL hash for shareable link (e.g. #doc/HBA-QUO-2026-0001) or query string ?doc=...
  const hash = window.location.hash;
  const searchParams = new URLSearchParams(window.location.search);
  const docParam = searchParams.get('doc') || searchParams.get('quotation') || searchParams.get('invoice');

  if (hash.startsWith('#doc/') || hash.startsWith('#quotation/') || hash.startsWith('#invoice/')) {
    const docNum = hash.split('/')[1];
    loadPublicDocument(docNum);
  } else if (docParam) {
    loadPublicDocument(docParam);
  }

  // Handle hash changes dynamically
  window.addEventListener('hashchange', () => {
    const newHash = window.location.hash;
    if (newHash.startsWith('#doc/') || newHash.startsWith('#quotation/') || newHash.startsWith('#invoice/')) {
      const docNum = newHash.split('/')[1];
      loadPublicDocument(docNum);
    }
  });

  async function loadPublicDocument(docNumber) {
    if (!publicOverlay) return;
    publicOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';

    try {
      const response = await fetch(`${INVOICE_API}?action=public_get&num=${encodeURIComponent(docNumber)}`);
      const result = await response.json();

      if (result.success && result.data) {
        renderPublicSheet(result.data, result.settings || {});
      } else {
        showPublicError('Document not found or invalid public link.');
      }
    } catch (err) {
      showPublicError('Document loading offline. Please check link URL.');
    }
  }

  function renderPublicSheet(record, settings) {
    const innerData = record.doc_data || {};
    const lineItems = innerData.lineItems || [];

    const isQuo = record.doc_type === 'quotation';
    document.getElementById('pub-doc-header-type').textContent = isQuo ? 'OFFICIAL QUOTATION' : 'INVOICE';
    document.getElementById('pub-doc-number').textContent = record.doc_number || '';
    document.getElementById('pub-client-org').textContent = record.client_org || record.client_name || '';
    document.getElementById('pub-client-name').textContent = record.client_name ? `Attn: ${record.client_name}` : '';
    document.getElementById('pub-client-email').textContent = record.client_email || record.client_address || '';
    document.getElementById('pub-issue-date').textContent = record.issue_date || '-';
    document.getElementById('pub-due-date').textContent = record.due_date || '-';

    // Render Line Items Table
    const tbody = document.getElementById('pub-line-items-body');
    if (tbody) {
      tbody.innerHTML = '';
      let subtotal = 0;

      lineItems.forEach(item => {
        const itemTotal = (item.quantity * item.rate);
        subtotal += itemTotal;
        const row = document.createElement('tr');
        row.innerHTML = `
          <td style="padding:0.875rem 1rem; border-bottom:1px solid #E2E0E8; font-weight:500;">${escapeHtml(item.description)}</td>
          <td style="padding:0.875rem 1rem; border-bottom:1px solid #E2E0E8;">${item.quantity}</td>
          <td style="padding:0.875rem 1rem; border-bottom:1px solid #E2E0E8;">$${parseFloat(item.rate).toFixed(2)}</td>
          <td style="padding:0.875rem 1rem; border-bottom:1px solid #E2E0E8; text-align:right; font-weight:700;">$${itemTotal.toFixed(2)}</td>
        `;
        tbody.appendChild(row);
      });

      const taxRate = parseFloat(innerData.taxRate || 0);
      const taxAmount = subtotal * (taxRate / 100);
      const discountVal = parseFloat(innerData.discountVal || 0);
      const grandTotal = Math.max(0, subtotal + taxAmount - discountVal);

      document.getElementById('pub-val-subtotal').textContent = `$${subtotal.toFixed(2)}`;
      document.getElementById('pub-val-tax').textContent = `$${taxAmount.toFixed(2)}`;
      document.getElementById('pub-val-grandtotal').textContent = `$${grandTotal.toFixed(2)}`;
    }

    // Company Bank Details from Settings
    if (settings.bank_name) {
      document.getElementById('pub-bank-details').innerHTML = `
        <strong>Account Name:</strong> ${escapeHtml(settings.account_name || 'Hope Base Advisory LLC')}<br>
        <strong>Bank Name:</strong> ${escapeHtml(settings.bank_name)}<br>
        <strong>Swift / IBAN:</strong> ${escapeHtml(settings.swift || '')} / ${escapeHtml(settings.iban || '')}<br>
        <strong>Currency:</strong> USD ($)
      `;
    }

    // Terms
    if (settings.terms_text) {
      document.getElementById('pub-terms-text').textContent = settings.terms_text;
    }

    // Authorized Handwritten Signature
    const sigEl = document.getElementById('pub-sig-img');
    let sigData = record.signature_data;
    if (sigEl) {
      if (sigData && sigData.startsWith('data:image/')) {
        sigEl.src = sigData;
        sigEl.style.display = 'block';
      } else {
        sigEl.src = '';
        sigEl.style.display = 'none';
      }
    }

    // Official Stamp
    const stampEl = document.getElementById('pub-stamp-img');
    const stampData = settings.company_stamp;
    if (stampEl) {
      if (stampData && stampData.startsWith('data:image/')) {
        stampEl.src = stampData;
        stampEl.style.display = 'block';
      } else {
        stampEl.src = '';
        stampEl.style.display = 'none';
      }
    }

    // Update Verification Reference
    document.querySelectorAll('#public-doc-modal .doc-verify-ref').forEach(el => {
      el.textContent = record.doc_number || 'HBA-DOC';
    });
  }

  function showPublicError(msg) {
    const container = document.getElementById('pub-printable-sheet');
    if (container) {
      container.innerHTML = `<div style="text-align:center; padding:4rem 2rem; color:#EF4444;"><h3>${escapeHtml(msg)}</h3></div>`;
    }
  }

  if (publicPrintBtn) {
    publicPrintBtn.addEventListener('click', () => {
      window.print();
    });
  }

  if (publicCloseBtn) {
    publicCloseBtn.addEventListener('click', () => {
      publicOverlay?.classList.remove('active');
      document.body.style.overflow = '';
    });
  }
}

function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[m]));
}
